import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { HAS_TRACKERS, INSTAGRAM_URL } from "../lib/siteConfig";
import { openCookieSettings } from "../lib/consent";

// Pages du site (mêmes que le menu, sans l'accueil)
const EXPLORE = [
  { to: "/articles", label: "Analyses" },
  { to: "/portefeuilles-sideb", label: "Portefeuilles Side B" },
  { to: "/cours", label: "Cours" },
  { to: "/simulateurs", label: "Outils" },
];

const LEGAL = [
  { to: "/mentions-legales", label: "Mentions légales", short: "Mentions légales" },
  { to: "/cgv", label: "Conditions générales de vente", short: "CGV" },
  { to: "/confidentialite", label: "Politique de confidentialité", short: "Confidentialité" },
];

function InstagramIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
    </svg>
  );
}

export default function Footer() {
  const linkCls = "text-zinc-500 hover:text-white transition-colors";
  return (
    <footer className="border-t border-white/5 sm:mt-12 bg-black" data-testid="app-footer">
      {/* Téléphone : version compacte (les sections du site sont dans la barre du bas) */}
      <div className="sm:hidden px-5 pt-7 pb-5" data-testid="footer-mobile">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" aria-label="Retour à l'accueil">
            <img src="/logo-sideb.png" alt="Side B" className="h-9 w-auto object-contain" />
          </Link>
          {INSTAGRAM_URL && (
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="grid place-items-center w-10 h-10 rounded-full border border-white/10 text-zinc-300"
              aria-label="Side B sur Instagram"
            >
              <InstagramIcon className="w-[18px] h-[18px]" />
            </a>
          )}
        </div>
        <nav className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-[13px]" aria-label="Informations légales">
          {LEGAL.map((l) => (
            <Link key={l.to} className={linkCls} to={l.to}>
              {l.short}
            </Link>
          ))}
          {HAS_TRACKERS && (
            <button type="button" onClick={openCookieSettings} className={linkCls}>
              Cookies
            </button>
          )}
        </nav>
        <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
          Contenus éducatifs : ni conseil en investissement, ni recommandation personnalisée. Investir, notamment en
          crypto-actifs, comporte un risque de perte en capital. Les performances passées ne préjugent pas des
          performances futures.
        </p>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-2 text-[11px] text-zinc-600">
          <span>© {new Date().getFullYear()} Side B</span>
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé par Stripe
          </span>
        </div>
      </div>

      <div className="hidden sm:grid mx-auto max-w-7xl px-6 py-12 gap-10 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.3fr]">
        <div>
          <Link to="/" className="inline-block" aria-label="Retour à l'accueil" data-testid="footer-logo">
            <img src="/logo-sideb.png" alt="Side B" className="h-12 w-auto object-contain" />
          </Link>
          <p className="text-sm text-zinc-500 mt-4 leading-relaxed max-w-xs">
            Apprendre à investir, comprendre les marchés financiers, et bâtir son patrimoine avec méthode.
          </p>
          {INSTAGRAM_URL && (
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-md border border-white/10 px-3 h-9 text-sm text-zinc-300 hover:text-white hover:border-white/25 transition-colors"
              aria-label="Side B sur Instagram"
              data-testid="footer-instagram"
            >
              <InstagramIcon /> Suivre sur Instagram
            </a>
          )}
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Explorer</h4>
          <ul className="space-y-2 text-sm">
            {EXPLORE.map((l) => (
              <li key={l.to}>
                <Link className={linkCls} to={l.to}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Légal</h4>
          <ul className="space-y-2 text-sm">
            {LEGAL.map((l) => (
              <li key={l.to}>
                <Link className={linkCls} to={l.to}>
                  {l.label}
                </Link>
              </li>
            ))}
            {HAS_TRACKERS && (
              <li>
                <button type="button" onClick={openCookieSettings} className={linkCls} data-testid="footer-cookie-settings">
                  Gérer les cookies
                </button>
              </li>
            )}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Avertissement</h4>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Contenus publiés à but éducatif. Ils ne constituent ni un conseil en investissement, ni une recommandation
            personnalisée. Investir, notamment en crypto-actifs, comporte un risque de perte en capital. Les performances
            passées ne préjugent pas des performances futures.
          </p>
        </div>
      </div>

      <div className="hidden sm:block border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-zinc-600">
          <span>© {new Date().getFullYear()} Side B — Tous droits réservés</span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé par Stripe
          </span>
        </div>
      </div>
    </footer>
  );
}
